import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Box, Dialog, InputBase, Tooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";

import menuCatalogUtil from "@plannel/pages/utils/menu-catalog-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import { FS } from "@plannel/utils/font-util";

/*
 * 메뉴 검색 — 상단바의 검색칸(발견용) + 그 칸이 여는 오버레이(실행용).
 *
 * 왜 사이드바가 아니라 여기인가:
 *   사이드바는 접힌 아이콘 레일이 기본이라, 거기 검색칸을 두면 **메뉴를 이미 연 사람만**
 *   쓸 수 있다 — 정작 줄이려던 왕복을 못 줄인다. 또 262px 로는 결과마다 붙는 경로 한 줄이
 *   안 들어가는데, 이 메뉴는 경로가 없으면 뜻이 안 닫힌다(아래 PathLine 주석).
 *
 * 입구가 둘인 이유:
 *   · 상단바 칸 — 안 알려줘도 눈에 띈다. 단축키만 있으면 아무도 못 찾는다.
 *   · Cmd/Ctrl+K — 매일 쓰는 사람이 마우스를 안 잡는다.
 *   둘이 같은 오버레이를 연다. 구현은 하나다.
 *
 * 데이터는 새로 만들지 않는다. App.js 가 이미 들고 있는 menuCatalogUtil 카탈로그를 그대로 받는다
 * (Agent Chat 이 화면 이동에 쓰는 그 목록). **권한 필터가 이미 걸려 있어** 열 수 없는 화면은
 * 애초에 후보에 안 뜬다 — 여기서 권한을 다시 판정하지 않는다.
 */

const RESULT_LIMIT = 50;

/*
 * 트리거 칸 = 진짜 입력칸처럼 보이되 실제로는 버튼이다. 입력은 오버레이 안에서만 받는다 —
 * 같은 칸을 두 곳에 두면 어느 쪽에 친 글자가 진짜인지 매번 헷갈린다.
 *
 * 생김새는 **상단바에 이미 있는 것에 맞춘다.** 이 툴바는 테두리 없는 아이콘과 글자뿐이라,
 * 테두리를 두른 입력칸을 끼워 넣으면 폼 조각 하나가 떨어져 있는 것처럼 혼자 뜬다(실제로 그랬다).
 *   · 테두리 없음 — 툴바에서 테두리를 가진 건 세그먼트(Small/Large) 하나뿐이다
 *   · 알약 모양 — 그 세그먼트와 같은 반경. 바로 옆에 서므로 각이 다르면 눈에 띈다
 *   · 높이 24 — 아이콘 버튼과 같은 키
 *   · 단축키 칩 없음 — 상자 안에 상자를 또 두면 겹이 셋이 된다. 단축키는 툴팁이 맡는다
 *     (그리고 Mac 기호 ⌘ 를 Windows 사용자에게 들이밀지 않게 된다 — 아래 shortcutLabel).
 */
const TRIGGER_SX = {
  display: "flex", alignItems: "center", gap: 0.75,
  height: 24, borderRadius: 999, cursor: "text", border: 0,
  /* FS.BODY — 이름표(breadcrumb)·세그먼트가 쓰는 FS.SUB 보다 한 단 아래다.
     이 글자는 **내용이 아니라 안내문**이라(입력하면 사라지는 자리) 옆의 실제 내용과 같은 크기로
     서면 툴바에서 자기 몫보다 세게 읽힌다. 숫자로 박지 않는다 — 폰트 토글을 따라가야 한다. */
  bgcolor: "action.hover", color: "text.secondary", fontSize: FS.BODY, minWidth: 0,
  "&:hover": {
    bgcolor: theme => alpha(theme.palette.primary.main, 0.09),
    color: "primary.main",
  },
};

/*
 * 단축키 표기 — Mac 은 ⌘K, 그 외(Windows·Linux)는 Ctrl K.
 *   ⌘ 는 Mac 자판에만 있는 기호다. 전 사용자에게 ⌘K 라고 적으면 Windows 쪽은 그게 무슨 키인지
 *   모른다. 동작은 metaKey·ctrlKey 를 둘 다 받으므로 어느 쪽 자판이든 그대로 눌리고,
 *   **표기만** 자기 자판의 이름으로 바뀐다.
 *   userAgent 로 판단한다 — navigator.platform 은 폐기 예정이다.
 */
const isMac = () => /Mac|iPhone|iPad/i.test(typeof navigator === "undefined" ? "" : navigator.userAgent);

/*
 * 결과 한 줄의 경로. lv1(모듈)은 바로 위 머리글(ModuleHeading)이 이미 말하므로 **lv2(그룹)만** 적는다.
 * 이 줄은 장식이 아니라 **구분 수단**이다 — "Settings"·"Dashboard"·"Simulation" 은 각각
 * 여러 모듈에서 같은 이름으로 쓰인다. 이름만 보여 주면 어느 화면인지 못 고른다.
 *   lv2 가 없는 leaf(lv1 바로 밑)는 이 줄을 아예 안 낸다 — 머리글이 말한 모듈명을 한 줄 아래
 *   그대로 되풀이하면 같은 말이 두 번 서고, 정작 lv2 가 있는 줄과 구분이 안 된다.
 */
const PathLine = ({ item }) => (item.lv2Label ? (
  <Box sx={{
    fontSize: FS.CAPTION, color: "text.disabled", lineHeight: 1.5,
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
  }}>
    {item.lv2Label}
  </Box>
) : null);

/*
 * 모듈(lv1) 머리글 — 목록을 **재정렬하지 않고**, 앞줄과 모듈이 달라지는 지점에만 낀다.
 *   묶어서 모으지 않는 이유: 이 목록의 순서는 rankByName 의 순위(완전일치→앞자리→포함→경로)다.
 *   모듈로 모으면 이름이 직접 맞은 화면보다 경로만 맞은 화면이 먼저 설 수 있다.
 *   같은 모듈이 순위 안에서 떨어져 있으면 머리글도 두 번 난다 — 그게 실제 순서다.
 *
 * sticky: 목록을 굴려도 지금 보는 줄이 어느 모듈인지 계속 보인다.
 * aria-hidden: role="listbox" 안에 option 이 아닌 줄을 두면 읽기 순서가 엉킨다. 모듈명은
 *   각 option 의 aria-label 이 따로 싣는다(아래 optionLabel).
 *
 * 색:
 *   · 색은 **띠와 왼쪽 막대**만 입고 글자는 text.secondary 로 둔다. 팔레트(사용자가 고른다)에는
 *     흰 바탕에서 대비가 아슬아슬한 색(예: calm 의 #C79A3F)이 섞여 있어, 글자에 그대로 쓰면
 *     어떤 팔레트에서는 머리글이 읽히지 않는다. 색은 "어느 모듈인지"만 말하면 된다.
 *   · 배경은 **불투명한 paper 위에 반투명 틴트를 덧칠**한다(backgroundImage). sticky 로 떠 있는
 *     동안 아래 줄이 비쳐 글자가 겹쳐 읽히는 걸 막으면서 색은 살린다 — 반투명 bgcolor 만으로는
 *     비친다.
 */
const ModuleHeading = ({ label, color, first }) => (
  <Box aria-hidden="true" sx={{
    position: "sticky", top: 0, zIndex: 1,
    /* 3px 막대 + 11px = 줄(px 1.75 = 14px)과 글자 시작점이 맞는다. 어긋나면 머리글만 들여쓴 것처럼 보인다. */
    pl: "11px", pr: 1.75, pt: first ? 0.25 : 0.375, pb: 0.375,
    mt: first ? 0 : 0.5,
    borderLeft: 3, borderLeftColor: color,
    bgcolor: "background.paper",
    backgroundImage: `linear-gradient(${alpha(color, 0.12)}, ${alpha(color, 0.12)})`,
    fontSize: FS.CAPTION, fontWeight: 600, color: "text.secondary",
    lineHeight: 1.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
  }}>
    {label}
  </Box>
);

/* 화면 낭독용 이름 — 눈으로는 머리글이 맡는 모듈명까지 한 줄에 실어 준다. */
const optionLabel = (item) => [item.name, item.lv1Label, item.lv2Label].filter(Boolean).join(", ");

/* 맞은 구간만 강조. 정규화(공백·중점 제거) 때문에 인덱스가 어긋나므로 위치는 카탈로그 유틸이 준다. */
const HitName = ({ name, query }) => {
  const range = menuCatalogUtil.matchRange(name, query);
  const body = range
    ? (
      <>
        {name.slice(0, range[0])}
        <Box component="mark" sx={{
          bgcolor: theme => alpha(theme.palette.warning.main, 0.28),
          color: "inherit", borderRadius: "2px", px: "1px",
        }}>{name.slice(range[0], range[1])}</Box>
        {name.slice(range[1])}
      </>
    )
    : name;

  return (
    <Box sx={{
      fontSize: FS.SUB, color: "text.primary", lineHeight: 1.5,
      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
    }}>{body}</Box>
  );
};

const MenuSearch = ({ menus, onSelect }) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  /* 조합 중(한글 자모가 아직 안 맺힌 상태)인지. 키 가로채기와 "없음" 문구를 둘 다 여기서 가른다. */
  const [composing, setComposing] = useState(false);
  const listRef = useRef(null);

  const items = useMemo(
    () => menuCatalogUtil.rankByName(menus, query, RESULT_LIMIT),
    [menus, query]);

  /*
   * 모듈별 색 — 순서는 **카탈로그(=사이드바) 순서**로 정한다. 검색 결과에 나온 순서로 번호를
   * 매기면 같은 모듈이 검색어마다 다른 색을 입어서, 색이 아무것도 뜻하지 않게 된다.
   * 팔레트는 차트와 같은 것을 쓴다(사용자가 Personal Settings 에서 고른 그 팔레트) — 여기서
   * 색을 새로 정하지 않는다.
   */
  const moduleColors = useMemo(() => {
    const map = new Map();
    (menus ?? []).forEach((m) => {
      if (m?.lv1Label && !map.has(m.lv1Label)) map.set(m.lv1Label, ColorUtil.colorSet(map.size, 500));
    });
    return map;
  }, [menus]);

  const closeSearch = useCallback(() => setOpen(false), []);

  const openSearch = useCallback(() => {
    /* 열 때마다 빈 칸으로 시작한다 — 지난 검색어가 남아 있으면 첫 타가 그 뒤에 붙는다. */
    setQuery("");
    setCursor(0);
    setOpen(true);
  }, []);

  const choose = useCallback((item) => {
    if (!item) return;
    closeSearch();
    /* 클릭해서 여는 것과 **같은 경로**로 연다(Sidebar 도 handleAddTab 을 부른다).
       검색으로 연 탭과 메뉴로 연 탭이 다르면 탭 중복·상태 불일치가 난다. */
    onSelect?.(item.key);
  }, [closeSearch, onSelect]);

  /* 전역 단축키. 입력칸에 포커스가 있어도 동작해야 하므로 target 을 가리지 않는다 —
     수식 키가 붙어 있어 글자 입력과 겹치지 않는다. preventDefault 는 필수다(브라우저 기본 동작 선점). */
  useEffect(() => {
    const onKeyDown = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey) return;
      if (e.key?.toLowerCase() !== "k") return;
      e.preventDefault();
      setOpen(prev => {
        if (!prev) { setQuery(""); setCursor(0); }
        return !prev;
      });
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  /* 목록이 바뀌면 커서를 맨 위로. 안 그러면 결과가 줄었을 때 커서가 목록 밖을 가리킨다. */
  useEffect(() => { setCursor(0); }, [query]);

  /* 키보드로 움직인 줄이 화면 밖이면 따라 내린다. block:"nearest" — 가운데 정렬은 한 줄 움직일
     때마다 목록 전체가 튀어서 어디까지 봤는지 놓친다. */
  useEffect(() => {
    const row = listRef.current?.querySelector(`[data-idx="${cursor}"]`);
    row?.scrollIntoView({ block: "nearest" });
  }, [cursor, items]);

  const handleListKeyDown = (e) => {
    /*
     * ⚠️ 한글·일본어·중국어 조합 중에는 이 핸들러가 **아무것도 가로채면 안 된다.**
     *   한글은 Enter 로 조합을 확정한다 — "수요" 를 치는 동안 누른 Enter 는 글자를 맺으려는
     *   것이지 화면을 열려는 게 아닌데, 막지 않으면 **첫 결과가 그대로 열려 버린다.**
     *   일본어·중국어 IME 는 ↑↓ 로 후보를 고르므로 화살표도 같이 넘긴다.
     *   isComposing 이 없는 구형 브라우저는 keyCode 229 로 같은 상태를 알린다.
     */
    if (e.nativeEvent?.isComposing || e.keyCode === 229) return;
    if (!items.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor(c => (c + 1) % items.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor(c => (c - 1 + items.length) % items.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      choose(items[cursor]);
    }
  };

  const shortcut = isMac() ? "⌘K" : "Ctrl K";

  return (
    <>
      {/* 좁아지면 돋보기 하나로 줄인다 — 상단바에서 이 칸이 이름표(breadcrumb)를 밀어내면 안 된다.
          단축키는 여기 적지 않고 툴팁이 자기 자판 이름으로 알려 준다(shortcutLabel 주석). */}
      <Tooltip title={`${t("menuSearch")} (${shortcut})`}>
        <Box
          component="button"
          type="button"
          onClick={openSearch}
          aria-label={`${t("menuSearch")} (${shortcut})`}
          sx={{
            ...TRIGGER_SX,
            width: { xs: 24, md: 140 },
            justifyContent: { xs: "center", md: "flex-start" },
            px: { xs: 0, md: 1 },
            pr: { md: 1.5 },
            /*
             * ⚠️ `font: "inherit"` 을 쓰지 않는다. font 는 **단축 속성이라 font-size 까지 같이 덮는다.**
             *   sx 에 fontSize 를 함께 적어도 소용없다 — MUI 의 styleFunctionSx 는 fontSize 같은
             *   "알고 있는" 스타일 prop 과 font 같은 생 CSS 를 다른 갈래로 처리해서, 최종 CSS 에서
             *   어느 쪽이 뒤에 오는지가 작성 순서와 달라질 수 있다. 실제로 font 가 이겨서
             *   라벨이 **body 기본값 16px** 로 서 있었다(이 앱은 CssBaseline 을 안 써서 body 가 16px).
             *   글자 크기를 아무리 바꿔도 화면이 그대로였던 게 이 때문이다.
             * 버튼의 UA 기본 글꼴만 걷어내면 되므로 fontFamily 만 상속받는다.
             */
            fontFamily: "inherit",
          }}
        >
          {/* 글자를 한 단 줄였으니 아이콘도 같이 줄인다 — 안 그러면 돋보기만 커 보인다 */}
          <SearchIcon sx={{ fontSize: 14, flex: "none" }} />
          {/* 크기는 글자를 그리는 이 요소에 **직접** 준다 — 부모에서 물려받게 두면 위 같은
              단축 속성 사고에 다시 걸린다. 여기 있는 한 무엇도 덮어쓸 수 없다. */}
          <Box sx={{
            display: { xs: "none", md: "block" }, flex: 1, textAlign: "left",
            fontSize: FS.BODY, lineHeight: 1.5,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {t("menuSearch")}
          </Box>
        </Box>
      </Tooltip>
      {/* 아이콘 묶음과 한 덩어리로 읽히지 않게 선 하나로 뗀다 — 이 칸만 성격이 다르다(입력 vs 실행) */}
      <Box sx={{ width: "1px", height: 18, bgcolor: "divider", flex: "none", mx: 0.5 }} />

      <Dialog
        open={open}
        onClose={closeSearch}
        fullWidth
        maxWidth="sm"
        /* 화면 한가운데가 아니라 위쪽에 띄운다 — 목록이 길어졌다 짧아졌다 할 때
           가운데 정렬이면 상자가 세로로 출렁여서 조준이 안 된다. */
        sx={{ "& .MuiDialog-container": { alignItems: "flex-start", pt: "10vh" } }}
        PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
      >
        <Box sx={{
          display: "flex", alignItems: "center", gap: 1.25,
          px: 1.75, py: 1.25, borderBottom: 1, borderColor: "divider",
        }}>
          <SearchIcon sx={{ fontSize: 18, color: "text.disabled", flex: "none" }} />
          <InputBase
            autoFocus
            fullWidth
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleListKeyDown}
            onCompositionStart={() => setComposing(true)}
            /* 값도 같이 맞춘다 — compositionend 와 onChange 의 발생 순서가 브라우저마다 달라
               (Chrome 은 end 가 먼저, Firefox 는 input 이 먼저) 한쪽만 믿으면 한 글자가 늦는다. */
            onCompositionEnd={(e) => { setComposing(false); setQuery(e.target.value); }}
            placeholder={t("menuSearch")}
            inputProps={{ "aria-label": t("menuSearch"), autoComplete: "off" }}
            sx={{ fontSize: FS.H2, "& input": { p: 0 } }}
          />
          {/* 여기에 Esc 칩을 또 두지 않는다 — 바닥 힌트(menuSearchKeyHint)가 이미 Esc 를 말한다 */}
        </Box>

        <Box ref={listRef} role="listbox" aria-label={t("menuSearch")}
             sx={{ maxHeight: 340, overflowY: "auto", py: 0.5 }}>
          {/* 안내 문구는 **검색어가 있고 조합이 끝났을 때만** 낸다.
              · 권한 목록(App.js)이 아직 안 온 사이에 열면 items 도 비는데, 그때
                "“” 와 일치하는 화면이 없습니다" 가 뜨면 고장으로 읽힌다.
              · 한글은 음절 사이에 자모만 남는 순간이 있다("수요" → ㅅ, 수, 수ㅇ, 수요).
                그 찰나마다 "없음" 이 깜빡이면 글자를 칠 때마다 화면이 튄다. */}
          {items.length === 0 ? (query.trim() && !composing && (
            <Box sx={{ px: 1.75, py: 2.5, fontSize: FS.SUB, color: "text.disabled" }}>
              {t("MSG_NoMenuMatch", { query })}
            </Box>
          )) : items.map((item, idx) => (
            <Fragment key={`${item.key}-${item.reduxKey ?? idx}`}>
              {(idx === 0 || items[idx - 1].lv1Label !== item.lv1Label) && (
                <ModuleHeading
                  label={item.lv1Label}
                  color={moduleColors.get(item.lv1Label) ?? "#9e9e9e"}
                  first={idx === 0}
                />
              )}
              <Box
                data-idx={idx}
                role="option"
                aria-selected={idx === cursor}
                aria-label={optionLabel(item)}
                onClick={() => choose(item)}
                /* hover 로 커서를 옮긴다 — 마우스를 올린 줄과 Enter 가 열 줄이 다르면 오클릭이 난다 */
                onMouseMove={() => setCursor(idx)}
                sx={{
                  px: 1.75, py: 0.75, cursor: "pointer", minWidth: 0,
                  /* 위로 훑을 때 sticky 머리글이 커서 줄을 덮지 않게 그 높이만큼 여유를 둔다
                     (scrollIntoView block:"nearest" 는 sticky 요소를 몰라서 딱 붙여 세운다). */
                  scrollMarginTop: "32px",
                  bgcolor: idx === cursor
                    ? theme => alpha(theme.palette.primary.main, 0.1)
                    : "transparent",
                }}
              >
                <HitName name={item.name} query={query} />
                <PathLine item={item} />
              </Box>
            </Fragment>
          ))}
        </Box>

        <Box sx={{
          display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap",
          px: 1.75, py: 0.875, borderTop: 1, borderColor: "divider", bgcolor: "action.hover",
          fontSize: FS.CAPTION, color: "text.disabled",
        }}>
          <Box>{t("menuSearchKeyHint")}</Box>
          {/* 검색 전에는 "50 / 205" 로 적지 않는다 — 50 은 걸러낸 수가 아니라 잘라낸 수라서
              마치 155개가 탈락한 것처럼 읽힌다. 검색 전에는 전체 수만 밝힌다. */}
          <Box sx={{ ml: "auto" }}>
            {query.trim() ? `${items.length} / ${menus?.length ?? 0}` : (menus?.length ?? 0)}
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default MenuSearch;
